import type { Drink, DrinkVariant } from "odyssey-shared";
import type { DrinkListener, LevelListener } from "../remote-characters.ts";
import type { VfxPlayer } from "./player.ts";

/**
 * The triggers that call the vfx player (ADR-134 decision 7): the level-up's and a potion's drink. A trigger knows an
 * event and names a vfx; it draws and sounds nothing.
 */

/**
 * The tint of each drink variant's burst, 0xRRGGBB (ADR-134's note of 2026-10-27, the colours the author approved): red
 * for health, a lemon yellow for Stamina that reads apart from the level-up's gold, green for a cure.
 */
export const DRINK_TINTS: Readonly<Record<DrinkVariant, number>> = Object.freeze({ health: 0xd04040, stamina: 0xf0e060, cure: 0x48b848 });

/**
 * The size another player's drink is drawn at: a drink's state carries no strength (ADR-074's amendment of 2026-10-27).
 */
export const WATCHED_DRINK_SCALE = 1;

/**
 * The page's own character reached a level (the `progress` frame, ADR-083): the burst on the character and the surge of
 * the HUD's XP bar, in time with each other.
 *
 * @param vfx The player.
 * @param handle The page's own handle.
 */
export function ownLevelUp(vfx: VfxPlayer, handle: number): void {
  vfx.play("levelUp", { handle, own: true });
  vfx.play("xpBarSurge", { handle, own: true });
}

/**
 * The listener `RemoteCharacters` tells when another player's level rises while it is drawn.
 *
 * @param vfx The player.
 * @returns The listener.
 */
export function remoteLevelUp(vfx: VfxPlayer): LevelListener {
  return (handle, _level, dx, dy) => {
    vfx.play("levelUp", { handle, own: false, dx, dy });
  };
}

/**
 * The page's own drink (the combat frame's `used` for a potion's record): the burst on its character in the variant's
 * tint, sized by the record's percent.
 *
 * @param vfx The player.
 * @param handle The page's own handle.
 * @param drink The record's `drink`.
 */
export function ownDrink(vfx: VfxPlayer, handle: number, drink: Drink): void {
  vfx.play("drinkBurst", { handle, own: true }, { tint: DRINK_TINTS[drink.variant], scale: drink.percent / 100 });
}

/**
 * The listener `RemoteCharacters` tells when another player's animation state changes to a drink state while it is
 * drawn. A variant this page has no tint for draws nothing.
 *
 * @param vfx The player.
 * @returns The listener.
 */
export function remoteDrink(vfx: VfxPlayer): DrinkListener {
  return (handle, variant, dx, dy) => {
    const tint = (DRINK_TINTS as Readonly<Record<string, number | undefined>>)[variant];
    if (tint !== undefined) {
      vfx.play("drinkBurst", { handle, own: false, dx, dy }, { tint, scale: WATCHED_DRINK_SCALE });
    }
  };
}
